import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Layout, Typography, Form, Input, Button, Row, Col, Space, Avatar, message, Spin, Tooltip, Modal } from 'antd';
import { UserOutlined, LockOutlined, MailOutlined, LoadingOutlined, InfoCircleOutlined, MailOutlined as MailIcon } from '@ant-design/icons';

const { Header, Content, Footer } = Layout;

const Register = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [isModalVisible, setIsModalVisible] = useState(false);

  const onFinish = async (values) => {
    setLoading(true);
    try {
      const response = await fetch('/backend/register.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(values),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Registration failed');
      }

      setIsModalVisible(true);
      form.resetFields();
    } catch (error) {
      message.error(error.message || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const onFinishFailed = (errorInfo) => {
    console.log('Form validation failed:', errorInfo.errors);
    message.error('Please correct the errors in the form.');
  };

  const passwordValidator = (_, value) => {
    if (!value) {
      setPasswordStrength(0);
      return Promise.reject(new Error('Please enter a password!'));
    }
    if (value.length < 6) {
      return Promise.reject(new Error('Password must be at least 6 characters!'));
    }
    if (!/[A-Z]/.test(value) || !/[0-9]/.test(value)) {
      return Promise.reject(new Error('Password must include an uppercase letter and a number!'));
    }
    return Promise.resolve();
  };

  const calculatePasswordStrength = (password) => {
    let strength = 0;
    if (password.length >= 6) strength += 25;
    if (/[A-Z]/.test(password)) strength += 25;
    if (/[0-9]/.test(password)) strength += 25;
    if (/[^A-Za-z0-9]/.test(password)) strength += 25;
    return strength;
  };

  const handlePasswordChange = (value) => {
    const strength = calculatePasswordStrength(value);
    setPasswordStrength(strength);
  };

  const getPasswordStrengthColor = () => {
    if (passwordStrength === 0) return 'gray';
    if (passwordStrength <= 50) return 'red';
    if (passwordStrength <= 75) return 'orange';
    return 'green';
  };

  const getBorderColor = (field) => {
    const errors = form.getFieldError(field);
    return errors.length > 0 ? '#dc2626' : '#10b981';
  };

  return (
    <Layout style={{ minHeight: '100vh', background: 'linear-gradient(135deg,rgb(249, 250, 252), #f9fafb)' }}>
      <Header
        style={{
          background: '#fff',
          padding: '0 30px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
          borderBottom: '1px solid #e5e7eb',
          transition: 'all 0.3s ease',
        }}
      >
        <Space align="center">
          <Avatar icon={<UserOutlined />} style={{ background: 'linear-gradient(45deg, #3b82f6, #60a5fa)', marginRight: 12 }} />
          <Typography.Title level={4} style={{ margin: 0, color: '#3b82f6', fontWeight: 700 }}>
            NH Guide
          </Typography.Title>
        </Space>
        <div style={{ flex: 1 }} />
        <Space size={20}>
          <Button
            type="link"
            href="/login"
            style={{ color: '#1a1a1a', fontWeight: 500, padding: '0 15px', fontSize: '16px' }}
          >
            Login
          </Button>
        </Space>
      </Header>

      <Content style={{ padding: '10px 20px' }}>
        <Row justify="center">
          <Col xs={24} sm={10} md={10} lg={15}>
            <div
              style={{
                background: 'linear-gradient(135deg, #ffffff, #f9fafb)',
                padding: '32px',
                borderRadius: '12px',
                border: '1px solid rgba(229, 231, 235, 0.5)',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
                transition: 'transform 0.2s ease, box-shadow 0.2s ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'scale(1.02)';
                e.currentTarget.style.boxShadow = '0 6px 25px rgba(0, 0, 0, 0.15)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'scale(1)';
                e.currentTarget.style.boxShadow = '0 4px 20px rgba(0, 0, 0, 0.1)';
              }}
            >
              <Typography.Title level={3} style={{ textAlign: 'center', color: '#1e293b', marginBottom: '20px', fontWeight: 700 }}>
                Registration
              </Typography.Title>
              <Typography.Text style={{ display: 'block', textAlign: 'center', color: '#64748b', marginBottom: '28px', fontSize: '16px' }}>
                Welcome! Create your account.
              </Typography.Text>
              <Form
                form={form}
                name="register"
                onFinish={onFinish}
                onFinishFailed={onFinishFailed}
                layout="vertical"
                style={{ maxWidth: '100%' }}
              >
                <Row gutter={24}>
                  <Col span={12}>
                    <Form.Item
                      label={<span id="firstName-label" style={{ color: '#1e293b', fontWeight: 500 }}>First Name</span>}
                      name="firstName"
                      rules={[{ required: true, message: 'Please enter your first name!' }]}
                    >
                      <Input
                        prefix={<UserOutlined style={{ color: '#94a3b8' }} />}
                        placeholder="Your first name"
                        style={{
                          height: '44px',
                          borderRadius: '8px',
                          borderColor: getBorderColor('firstName'),
                          transition: 'border-color 0.3s ease, box-shadow 0.3s ease',
                        }}
                        aria-labelledby="firstName-label"
                        onFocus={(e) => (e.target.style.boxShadow = '0 0 5px rgba(250, 251, 253, 0.5)')}
                        onBlur={(e) => (e.target.style.boxShadow = 'none')}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      label={<span id="lastName-label" style={{ color: '#1e293b', fontWeight: 500 }}>Last Name</span>}
                      name="lastName"
                      rules={[{ required: true, message: 'Please enter your last name!' }]}
                    >
                      <Input
                        prefix={<UserOutlined style={{ color: '#94a3b8' }} />}
                        placeholder="Your last name"
                        style={{
                          height: '44px',
                          borderRadius: '8px',
                          borderColor: getBorderColor('lastName'),
                          transition: 'border-color 0.3s ease, box-shadow 0.3s ease',
                        }}
                        aria-labelledby="lastName-label"
                        onFocus={(e) => (e.target.style.boxShadow = '0 0 5px rgba(59, 130, 246, 0.5)')}
                        onBlur={(e) => (e.target.style.boxShadow = 'none')}
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={24}>
                  <Col span Seahorse={12}>
                    <Form.Item
                      label={<span id="email-label" style={{ color: '#1e293b', fontWeight: 500 }}>Email Address</span>}
                      name="email"
                      rules={[
                        { required: true, message: 'Please enter your email!' },
                        { type: 'email', message: 'Please enter a valid email!' },
                      ]}
                    >
                      <Input
                        prefix={<MailOutlined style={{ color: '#94a3b8' }} />}
                        placeholder="your.email@example.com"
                        style={{
                          height: '44px',
                          borderRadius: '8px',
                          borderColor: getBorderColor('email'),
                          transition: 'border-color 0.3s ease, box-shadow 0.3s ease',
                        }}
                        aria-labelledby="email-label"
                        onFocus={(e) => (e.target.style.boxShadow = '0 0 5px rgba(59, 130, 246, 0.5)')}
                        onBlur={(e) => (e.target.style.boxShadow = 'none')}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      label={
                        <span id="password-label" style={{ color: '#1e293b', fontWeight: 500 }}>
                          Password
                          <Tooltip title="Must be at least 6 characters with an uppercase letter and a number">
                            <InfoCircleOutlined style={{ marginLeft: '8px', color: '#94a3b8' }} />
                          </Tooltip>
                        </span>
                      }
                      name="password"
                      rules={[{ validator: passwordValidator }]}
                    >
                      <Input.Password
                        prefix={<LockOutlined style={{ color: '#94a3b8' }} />}
                        placeholder="Your password"
                        style={{
                          height: '44px',
                          borderRadius: '8px',
                          borderColor: getBorderColor('password'),
                          transition: 'border-color 0.3s ease, box-shadow 0.3s ease',
                        }}
                        aria-labelledby="password-label"
                        onFocus={(e) => (e.target.style.boxShadow = '0 0 5px rgba(59, 130, 246, 0.5)')}
                        onBlur={(e) => (e.target.style.boxShadow = 'none')}
                        onChange={(e) => handlePasswordChange(e.target.value)}
                      />
                    </Form.Item>
                    {passwordStrength > 0 && (
                      <div
                        style={{
                          width: `${passwordStrength}%`,
                          height: '4px',
                          background: getPasswordStrengthColor(),
                          borderRadius: '2px',
                          marginTop: '4px',
                          transition: 'width 0.3s ease',
                        }}
                      />
                    )}
                  </Col>
                </Row>

                <Row gutter={24}>
                  <Col span={12}>
                    <Form.Item
                      label={<span id="confirmPassword-label" style={{ color: '#1e293b', fontWeight: 500 }}>Confirm Password</span>}
                      name="confirmPassword"
                      dependencies={['password']}
                      rules={[
                        { required: true, message: 'Please confirm your password!' },
                        ({ getFieldValue }) => ({
                          validator(_, value) {
                            if (!value || getFieldValue('password') === value) {
                              return Promise.resolve();
                            }
                            return Promise.reject(new Error('Passwords do not match!'));
                          },
                        }),
                      ]}
                    >
                      <Input.Password
                        prefix={<LockOutlined style={{ color: '#94a3b8' }} />}
                        placeholder="Confirm your password"
                        style={{
                          height: '44px',
                          borderRadius: '8px',
                          borderColor: getBorderColor('confirmPassword'),
                          transition: 'border-color 0.3s ease, box-shadow 0.3s ease',
                        }}
                        aria-labelledby="confirmPassword-label"
                        onFocus={(e) => (e.target.style.boxShadow = '0 0 5px rgba(59, 130, 246, 0.5)')}
                        onBlur={(e) => (e.target.style.boxShadow = 'none')}
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <Form.Item>
                  <Button
                    type="primary"
                    htmlType="submit"
                    style={{
                      width: '100%',
                      height: '48px',
                      background: 'linear-gradient(90deg, #3b82f6, #60a5fa)',
                      border: 'none',
                      borderRadius: '8px',
                      fontWeight: 600,
                      color: '#fff',
                      boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)',
                      transition: 'all 0.3s ease',
                    }}
                    disabled={loading}
                    onMouseEnter={(e) => (e.target.style.background = 'linear-gradient(90deg, #2563eb, #3b82f6)')}
                    onMouseLeave={(e) => (e.target.style.background = 'linear-gradient(90deg, #3b82f6, #60a5fa)')}
                  >
                    {loading ? <Spin indicator={<LoadingOutlined style={{ color: '#fff' }} />} /> : 'Register'}
                  </Button>
                </Form.Item>

                <Typography.Text style={{ display: 'block', textAlign: 'center', color: '#64748b', fontSize: '14px' }}>
                  Already have an account?{' '}
                  <Link to="/login" style={{ color: '#3b82f6', fontWeight: 500, textDecoration: 'none', transition: 'color 0.3s ease' }}>
                    Log in here
                  </Link>
                </Typography.Text>
              </Form>
            </div>
          </Col>
        </Row>
      </Content>

      <Footer
        style={{
          textAlign: 'center',
          padding: '20px 0',
          background: '#fff',
          boxShadow: '0 -2px 10px rgba(0, 0, 0, 0.05)',
          borderTop: '1px solid #e5e7eb',
          color: '#64748b',
        }}
      >
        <Space size={24}>
          <Button type="link" href="/support" style={{ color: '#3b82f6', fontWeight: 500 }}>
            Support
          </Button>
          <Button type="link" href="/support" style={{ color: '#3b82f6', fontWeight: 500 }}>
            ACHRA CoA Assistant
          </Button>
          <Button type="link" href="/privacy" style={{ color: '#3b82f6', fontWeight: 500 }}>
            View Privacy Policy
          </Button>
          <Button type="link" href="/resources" style={{ color: '#3b82f6', fontWeight: 500 }}>
            Access Resources
          </Button>
          <Button type="link" href="/courses" style={{ color: '#3b82f6', fontWeight: 500 }}>
            Courses
          </Button>
        </Space>
      </Footer>

      <Modal
        title={
          <div
            style={{
              background: 'linear-gradient(90deg, #3b82f6, #60a5fa)',
              color: '#fff',
              padding: '15px 25px',
              borderRadius: '12px 12px 0 0',
              display: 'flex',
              alignItems: 'center',
              fontSize: '18px',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '1px',
              transition: 'all 0.3s ease',
            }}
          >
            <MailIcon style={{ marginRight: '15px', fontSize: '22px', transform: 'rotate(-10deg)', transition: 'transform 0.3s ease' }} />
            Email Confirmation
          </div>
        }
        open={isModalVisible}
        footer={
          <Button
            type="primary"
            onClick={() => setIsModalVisible(false)}
            style={{
              background: 'linear-gradient(90deg, #2563eb, #3b82f6)',
              border: 'none',
              borderRadius: '8px',
              width: '100%',
              height: '45px',
              fontSize: '16px',
              fontWeight: 500,
              color: '#fff',
              transition: 'background 0.3s ease, transform 0.2s ease',
              boxShadow: '0 4px 12px rgba(37, 99, 235, 0.3)',
            }}
            onMouseEnter={(e) => {
              e.target.style.background = 'linear-gradient(90deg, #1d4ed8, #2563eb)';
              e.target.style.transform = 'scale(1.05)';
            }}
            onMouseLeave={(e) => {
              e.target.style.background = 'linear-gradient(90deg, #2563eb, #3b82f6)';
              e.target.style.transform = 'scale(1)';
            }}
          >
            Close
          </Button>
        }
        onCancel={() => setIsModalVisible(false)}
        style={{
          top: '20%',
          borderRadius: '15px',
          boxShadow: '0 15px 30px rgba(0, 0, 0, 0.15)',
          overflow: 'hidden',
          transition: 'transform 0.3s ease, box-shadow 0.3s ease',
        }}
        bodyStyle={{
          padding: '25px',
          textAlign: 'center',
          fontSize: '16px',
          color: '#1e293b',
          lineHeight: '1.6',
          fontFamily: '"Roboto", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
        }}
      >
        <p style={{ marginBottom: '15px', fontWeight: 400 }}>
          A confirmation link has been sent to your email. <strong>Check your inbox</strong> (or Papercut locally).
        </p>
        <p style={{ color: '#64748b', fontStyle: 'italic', fontSize: '14px' }}>
          Make sure to check your spam folder if necessary.
        </p>
      </Modal>
    </Layout>
  );
};

export default Register;